import { BlockEditProps } from "@wordpress/blocks";
import { MediaPlaceholder, MediaUpload } from "@wordpress/block-editor";
import React, { useEffect } from "react";
import { Placeholder } from "@wordpress/components";
import useIsSelected from "../hooks/useIsSelected";

type Prefixed<T extends Record<string, unknown>, TPrefix extends string> = {
  [K in keyof T as K extends string ? `${TPrefix}${K}` : never]: T[K];
};

type FileProps = {
  Url?: string | undefined;
  Id?: number | undefined;
  Alt?: string | undefined;
};

export type FileEditorProps<TPrefix extends string = "file"> = Pick<
  BlockEditProps<Prefixed<FileProps, TPrefix>>,
  "attributes" | "setAttributes"
> & {
  prefix: TPrefix;
  placeholderClassName?: string;
};

export default function FileEditor<T extends string>(
  props: FileEditorProps<T>
) {
  function getThumbnail(url: string | undefined) {
    const fileName = url?.substring(url.lastIndexOf("/") + 1);
    const fileExtension = fileName?.split(".").pop();
    const newExtension = `-${fileExtension}.jpg`;
    const thumbnailUrl = url?.replace(`.${fileExtension}`, newExtension);
    return thumbnailUrl;
  }

  const isSelected = useIsSelected();
  const urlKey = `${props.prefix}Url` as const;
  const id = props.attributes[`${props.prefix}Id`] as number | undefined;
  // eslint-disable-next-line prettier/prettier
  const thumbnailKey = `${props.prefix}Thumbnail` as const;
  const fileThumbnailUrl = getThumbnail(
    props.attributes[`${props.prefix}Url`] as string | undefined
  );
  const url = props.attributes[`${props.prefix}Url`] as string | undefined;
  const alt = props.attributes[`${props.prefix}Alt`] as string | undefined;

  return (
    <MediaPlaceholder
      onSelect={(el) => {
        props.setAttributes({
          [urlKey]: el.url as string,
        } as Partial<Prefixed<FileProps, T>>);
      }}
      onHTMLDrop={undefined}
      // @ts-ignore
      placeholder={(content) =>
        url ? (
          <div className="yt-image-editor-placeholder">
            <img
              src={fileThumbnailUrl}
              alt={alt}
              height={400 + "px"}
              className="mb-4"
            />
            <MediaUpload
              onSelect={(el) => {
                props.setAttributes({
                  [urlKey]: el.url as string,
                } as unknown as Partial<Prefixed<FileProps, T>>);
              }}
              //allowedTypes={['image']}
              value={id}
              render={({ open }) => (
                <div className="yt-image-editor-toolbar">
                  <button
                    className="components-button editor-post-featured-image__action"
                    onClick={open}
                  >
                    Ersetzen
                  </button>
                  <button
                    className="components-button editor-post-featured-image__action"
                    onClick={() =>
                      props.setAttributes({
                        [urlKey]: undefined,
                        [thumbnailKey]: undefined,
                      } as Partial<Prefixed<FileProps, T>>)
                    }
                  >
                    Entfernen
                  </button>
                </div>
              )}
            />
          </div>
        ) : (
          <Placeholder
            withIllustration={!isSelected}
            className={props.placeholderClassName}
            label="Datei auswählen"
          >
            {content}
          </Placeholder>
        )
      }
      className={props.placeholderClassName}
      mediaPreview={<img src={url} alt={alt} />}
      //accept="image/*"
    />
  );
}
